import React, { useEffect, useState, useCallback } from 'react';
import { xeroAPI } from '../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from './ui/alert-dialog';
import { FileText, Trash2, RefreshCw, ExternalLink, CheckCircle2, AlertTriangle, BadgePoundSterling, Plus, Unlink } from 'lucide-react';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { LinkXeroInvoiceDialog } from './LinkXeroInvoiceDialog';
import { formatCurrency, formatDate } from '../lib/utils';
import { toast } from 'sonner';

const MANUAL_METHODS = [
  { value: 'bank_transfer', label: 'Bank transfer' },
  { value: 'card', label: 'Card' },
  { value: 'cash', label: 'Cash' },
  { value: 'cheque', label: 'Cheque' },
  { value: 'other', label: 'Other' },
];

const STATUS_STYLES = {
  PAID: 'bg-emerald-100 text-emerald-700 border-emerald-200',
  AUTHORISED: 'bg-blue-100 text-blue-700 border-blue-200',
  SUBMITTED: 'bg-blue-100 text-blue-700 border-blue-200',
  DRAFT: 'bg-slate-100 text-slate-600 border-slate-200',
  SENT: 'bg-amber-100 text-amber-700 border-amber-200',
  VOIDED: 'bg-red-100 text-red-600 border-red-200',
  DELETED: 'bg-red-100 text-red-600 border-red-200',
};

const PAYMENT_METHOD_LABELS = {
  card: 'Paid by card (Stripe)',
  bank_transfer: 'Paid by bank transfer',
};

const paymentMethodLabel = (inv) => {
  if (inv.payment_method && PAYMENT_METHOD_LABELS[inv.payment_method]) {
    return PAYMENT_METHOD_LABELS[inv.payment_method];
  }
  // Fallback for legacy rows without an explicit payment_method.
  const via = (inv.paid_via || '').toLowerCase();
  if (via.startsWith('stripe')) return 'Paid by card (Stripe)';
  if (via === 'xero' || via === 'xero_sync') return 'Paid by bank transfer';
  return null;
};

export const XeroInvoicesCard = ({ customerId, isAdmin, lastReconciledAt }) => {
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [deletingId, setDeletingId] = useState(null);
  const [reconciledAt, setReconciledAt] = useState(lastReconciledAt || null);
  const [linkOpen, setLinkOpen] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await xeroAPI.getCustomerInvoices(customerId);
      setInvoices(Array.isArray(res.data) ? res.data : []);
    } catch (e) {
      setInvoices([]);
    } finally {
      setLoading(false);
    }
  }, [customerId]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { setReconciledAt(lastReconciledAt || null); }, [lastReconciledAt]);

  const [syncing, setSyncing] = useState(false);
  const [markOpen, setMarkOpen] = useState(null);
  const [markMethod, setMarkMethod] = useState('bank_transfer');
  const [marking, setMarking] = useState(false);

  const markPaid = async () => {
    if (!markOpen) return;
    setMarking(true);
    try {
      await xeroAPI.markPaidManual(markOpen.id, markMethod);
      toast.success(`${markOpen.xero_invoice_number || 'Invoice'} marked paid — reflected on the CRM, customer portal & Xero`);
      setMarkOpen(null);
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not mark invoice paid');
    } finally {
      setMarking(false);
    }
  };

  const syncFromXero = async () => {
    setSyncing(true);
    try {
      const res = await xeroAPI.syncPayments(customerId);
      const r = res.data?.result || {};
      const fixed = (r.marked_paid || 0) + (r.deposit_marked || 0) + (r.stripe_marked_paid || 0) + (r.xero_backfilled || 0);
      const stuck = r.xero_still_unsynced || 0;
      const draftBlocked = r.xero_draft_blocked || 0;
      if (draftBlocked > 0) {
        toast.warning(`${fixed > 0 ? `${fixed} settled. ` : ''}${draftBlocked} Stripe-paid invoice${draftBlocked === 1 ? ' is' : 's are'} still DRAFT in Xero — approve ${draftBlocked === 1 ? 'it' : 'them'} in Xero, then Reconcile again.`);
      } else if (stuck > 0) {
        toast.warning(`${fixed > 0 ? `${fixed} settled, but ` : ''}${stuck} Stripe payment${stuck === 1 ? '' : 's'} couldn't post to Xero. Check a payments-enabled Stripe bank account is set in Xero settings.`);
      } else {
        toast.success(
          fixed > 0
            ? `Reconciled — ${fixed} payment${fixed === 1 ? '' : 's'} settled (Xero + Stripe).`
            : 'Reconciled — everything already up to date across Xero & Stripe.'
        );
      }
      if (res.data?.reconciled_at) setReconciledAt(res.data.reconciled_at);
      if (Array.isArray(res.data?.invoices)) setInvoices(res.data.invoices);
      else await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Reconciliation failed');
    } finally {
      setSyncing(false);
    }
  };

  const handleDelete = async (inv) => {
    setDeletingId(inv.id);
    try {
      const res = await xeroAPI.deleteInvoice(inv.id, true);
      toast.success(
        res.data?.voided_in_xero
          ? `${inv.xero_invoice_number || 'Invoice'} removed and voided in Xero`
          : `${inv.xero_invoice_number || 'Invoice'} removed from CRM`
      );
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not delete invoice');
    } finally {
      setDeletingId(null);
    }
  };

  const handleUnlink = async (inv) => {
    setDeletingId(inv.id);
    try {
      await xeroAPI.deleteInvoice(inv.id, false);
      toast.success(`${inv.xero_invoice_number || 'Invoice'} unlinked from this customer (not changed in Xero)`);
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not unlink invoice');
    } finally {
      setDeletingId(null);
    }
  };

  const invoiceDocs = invoices.filter((i) => i.doc_type !== 'quote');
  const quoteDocs = invoices.filter((i) => i.doc_type === 'quote');

  const renderRow = (inv) => {
    const status = (inv.status || 'DRAFT').toUpperCase();
    const isPaid = status === 'PAID';
    const isQuote = inv.doc_type === 'quote';
    // Reconciled = paid AND either posted to Xero from Stripe, or marked paid by Xero itself.
    const reconciled = isPaid && !isQuote && (inv.xero_payment_applied === true || inv.paid_via === 'xero' || inv.paid_via === 'xero_sync');
    const notSynced = isPaid && !isQuote && (inv.paid_via || '').toLowerCase().startsWith('stripe') && inv.xero_payment_applied !== true;
    const draftBlocked = notSynced && inv.xero_draft_blocked === true;
    const methodLabel = isPaid && !isQuote ? paymentMethodLabel(inv) : null;
    return (
      <div
        key={inv.id}
        className="flex items-center justify-between gap-3 p-3 rounded-lg border border-slate-200 bg-white"
        data-testid={`xero-invoice-row-${inv.id}`}
      >
        <div className="flex items-center gap-3 min-w-0">
          <FileText className="w-4 h-4 text-slate-400 flex-shrink-0" />
          <div className="min-w-0">
            <p className="text-sm font-medium text-slate-900 truncate">
              {inv.xero_invoice_number || inv.description || 'Untitled'}
              {inv.invoice_type && (
                <span className="ml-2 text-xs font-normal text-slate-400 capitalize">
                  {inv.invoice_type.replace('_', ' ')}
                </span>
              )}
            </p>
            <p className="text-xs text-slate-500">
              {formatCurrency(inv.amount)}
              {inv.created_at && <span className="ml-2">· {formatDate(inv.created_at)}</span>}
            </p>
            {methodLabel && (
              <p className="text-xs text-slate-600 mt-0.5 flex items-center gap-1" data-testid={`xero-invoice-method-${inv.id}`}>
                <CheckCircle2 className="w-3.5 h-3.5 flex-shrink-0 text-slate-400" /> {methodLabel}
              </p>
            )}
            {reconciled && (
              <p className="text-xs text-emerald-600 mt-0.5 flex items-center gap-1" data-testid={`xero-invoice-reconciled-${inv.id}`}>
                <CheckCircle2 className="w-3.5 h-3.5 flex-shrink-0" /> Reconciled in Xero
              </p>
            )}
            {notSynced && (
              draftBlocked ? (
                <p className="text-xs text-orange-600 mt-0.5 flex items-start gap-1" data-testid={`xero-invoice-draftblocked-${inv.id}`}>
                  <AlertTriangle className="w-3.5 h-3.5 flex-shrink-0 mt-0.5" /> Paid via Stripe, but the invoice is still a DRAFT in Xero — approve it in Xero, then Reconcile to record the payment.
                </p>
              ) : (
                <p className="text-xs text-amber-600 mt-0.5 flex items-center gap-1" data-testid={`xero-invoice-notsynced-${inv.id}`}>
                  <AlertTriangle className="w-3.5 h-3.5 flex-shrink-0" /> Paid via Stripe — not yet synced to Xero
                </p>
              )
            )}
          </div>
        </div>
        <div className="flex items-center gap-2 flex-shrink-0">
          <Badge variant="outline" className={STATUS_STYLES[status] || 'bg-slate-100 text-slate-600'} data-testid={`xero-invoice-status-${inv.id}`}>
            {status}
          </Badge>
          {isAdmin && !isPaid && !isQuote && (
            <Button
              variant="outline"
              size="sm"
              className="h-8 border-emerald-300 text-emerald-700 hover:bg-emerald-50"
              onClick={() => { setMarkMethod('bank_transfer'); setMarkOpen(inv); }}
              data-testid={`xero-invoice-markpaid-${inv.id}`}
              title="Manually mark this invoice as paid"
            >
              <BadgePoundSterling className="w-3.5 h-3.5 mr-1" /> Mark paid
            </Button>
          )}
          {isAdmin && (inv.linked_from_xero || inv.linked_manually) && (
            <AlertDialog>
              <AlertDialogTrigger asChild>
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-8 w-8 text-slate-400 hover:text-amber-600 hover:bg-amber-50"
                  disabled={deletingId === inv.id}
                  data-testid={`xero-invoice-unlink-${inv.id}`}
                  title="Unlink from this customer (does not change Xero)"
                >
                  <Unlink className="w-4 h-4" />
                </Button>
              </AlertDialogTrigger>
              <AlertDialogContent data-testid="xero-invoice-unlink-dialog">
                <AlertDialogHeader>
                  <AlertDialogTitle>Unlink this invoice?</AlertDialogTitle>
                  <AlertDialogDescription>
                    This removes <strong>{inv.xero_invoice_number || 'the invoice'}</strong> from this customer in the CRM only.
                    The invoice stays exactly as-is in Xero — nothing is voided. Use this if it was attached to the wrong customer.
                  </AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                  <AlertDialogCancel data-testid={`xero-invoice-unlink-cancel-${inv.id}`}>Cancel</AlertDialogCancel>
                  <AlertDialogAction
                    onClick={() => handleUnlink(inv)}
                    className="bg-amber-600 hover:bg-amber-700"
                    data-testid={`xero-invoice-unlink-confirm-${inv.id}`}
                  >
                    Unlink from customer
                  </AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>
          )}
          {isAdmin && (
            <AlertDialog>
              <AlertDialogTrigger asChild>
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-8 w-8 text-slate-400 hover:text-red-600 hover:bg-red-50"
                  disabled={deletingId === inv.id}
                  data-testid={`xero-invoice-delete-${inv.id}`}
                  title="Delete duplicate / void in Xero"
                >
                  <Trash2 className="w-4 h-4" />
                </Button>
              </AlertDialogTrigger>
              <AlertDialogContent data-testid="xero-invoice-delete-dialog">
                <AlertDialogHeader>
                  <AlertDialogTitle>Delete this {inv.doc_type === 'quote' ? 'quote' : 'invoice'}?</AlertDialogTitle>
                  <AlertDialogDescription>
                    This removes <strong>{inv.xero_invoice_number || 'the record'}</strong> from the CRM and
                    {inv.doc_type === 'quote' ? ' marks the quote DELETED' : ' voids the invoice'} in Xero.
                    Use this only for duplicates. This cannot be undone.
                  </AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                  <AlertDialogCancel data-testid="xero-invoice-delete-cancel">Cancel</AlertDialogCancel>
                  <AlertDialogAction
                    onClick={() => handleDelete(inv)}
                    className="bg-red-600 hover:bg-red-700"
                    data-testid="xero-invoice-delete-confirm"
                  >
                    Delete & void in Xero
                  </AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>
          )}
        </div>
      </div>
    );
  };

  return (
    <Card className="border-slate-200" data-testid="xero-invoices-card">
      <CardHeader className="flex flex-row items-center justify-between pb-2">
        <CardTitle className="text-lg font-semibold font-['Manrope'] flex items-center gap-2">
          <ExternalLink className="w-5 h-5" />
          Xero Documents
        </CardTitle>
        <div className="flex items-center gap-1">
          {isAdmin && (
            <Button
              variant="outline"
              size="sm"
              className="h-8 border-blue-300 text-blue-700 hover:bg-blue-50"
              onClick={() => setLinkOpen(true)}
              data-testid="xero-link-invoice-btn"
              title="Search Xero and link an existing invoice to this customer"
            >
              <Plus className="w-3.5 h-3.5 mr-1" />
              Add existing invoice
            </Button>
          )}
          {isAdmin && (
            <Button
              variant="outline"
              size="sm"
              className="h-8 border-emerald-300 text-emerald-700 hover:bg-emerald-50"
              onClick={syncFromXero}
              disabled={syncing || loading}
              data-testid="xero-sync-payments-btn"
              title="Reconcile payments across Xero, Stripe and the CRM"
            >
              <RefreshCw className={`w-3.5 h-3.5 mr-1 ${syncing ? 'animate-spin' : ''}`} />
              {syncing ? 'Reconciling…' : 'Reconcile payments'}
            </Button>
          )}
          <Button variant="ghost" size="sm" onClick={load} disabled={loading} data-testid="xero-invoices-refresh">
            <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
          </Button>
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        {reconciledAt && (
          <p className="text-xs text-slate-400" data-testid="xero-reconciled-at">
            Last reconciled {formatDate(reconciledAt)}
          </p>
        )}
        {loading ? (
          <p className="text-sm text-slate-400">Loading…</p>
        ) : invoices.length === 0 ? (
          <p className="text-sm text-slate-400 italic" data-testid="xero-invoices-empty">
            No Xero invoices or quotes sent to this customer yet.
          </p>
        ) : (
          <>
            {invoiceDocs.length > 0 && (
              <div className="space-y-2">
                <p className="text-xs font-medium text-slate-500 uppercase tracking-wide">Invoices ({invoiceDocs.length})</p>
                {invoiceDocs.map(renderRow)}
              </div>
            )}
            {quoteDocs.length > 0 && (
              <div className="space-y-2">
                <p className="text-xs font-medium text-slate-500 uppercase tracking-wide">Quotes ({quoteDocs.length})</p>
                {quoteDocs.map(renderRow)}
              </div>
            )}
          </>
        )}
      </CardContent>
      <AlertDialog open={!!markOpen} onOpenChange={(o) => { if (!o) setMarkOpen(null); }}>
        <AlertDialogContent data-testid="xero-markpaid-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Mark invoice as paid</AlertDialogTitle>
            <AlertDialogDescription>
              Mark <strong>{markOpen?.xero_invoice_number || 'this invoice'}</strong> ({formatCurrency(markOpen?.amount)}) as paid.
              This updates the CRM, the customer portal, and posts the payment into Xero.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <div className="space-y-1.5 py-2">
            <label className="text-xs font-medium text-slate-500">Payment method</label>
            <Select value={markMethod} onValueChange={setMarkMethod}>
              <SelectTrigger data-testid="xero-markpaid-method"><SelectValue /></SelectTrigger>
              <SelectContent>
                {MANUAL_METHODS.map((m) => <SelectItem key={m.value} value={m.value}>{m.label}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="xero-markpaid-cancel">Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={(e) => { e.preventDefault(); markPaid(); }}
              disabled={marking}
              className="bg-emerald-600 hover:bg-emerald-700"
              data-testid="xero-markpaid-confirm"
            >
              {marking ? 'Saving…' : 'Mark paid'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
      <LinkXeroInvoiceDialog customerId={customerId} open={linkOpen} onOpenChange={setLinkOpen} onLinked={load} />
    </Card>
  );
};

export default XeroInvoicesCard;
